/**
 * AuthShell — the shared dark V2 wrapper for the PUBLIC auth screens
 * (/signup, /login).
 *
 * These routes render OUTSIDE the V2 shell and OUTSIDE the v2Enabled toggle,
 * so they cannot rely on the shell setting <html data-theme>. The shell wraps
 * its subtree in `.v2-root.v2-scope[data-theme="dark"][data-accent="lime"]`,
 * which resolves the full dark token set locally (see the additive
 * `.v2-scope[...]` selectors in v2-tokens.css). v2-tokens.css is imported here
 * directly so the tokens are present on the public route bundle.
 *
 * IMPORTANT: this must be a MODULE-SCOPE component. It was previously an
 * inline `const Shell = …` inside the signup render — a new component identity
 * per keystroke, which remounted the form and dropped input focus after every
 * character (the "can only type one letter" bug).
 */
import type { ReactNode } from 'react';

import { Logo } from './Logo';
import { SUPPORT_EMAIL } from '../../lib/constants';
import '../styles/v2-tokens.css'; // public routes render outside the shell — load tokens here

/** Dark V2 wrapper for public auth screens. */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="v2-root v2-scope min-h-screen w-full flex items-center justify-center bg-[var(--bg-0)] p-4"
      data-theme="dark"
      data-accent="lime"
    >
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2 text-[var(--tx-hi)]">
          <Logo size={26} />
          <span className="text-[15px] font-semibold tracking-[-0.01em]">Antero Trail</span>
        </div>
        <div className="rounded-[var(--r-xl)] border border-[var(--bd-subtle)] bg-[var(--bg-2)] p-7 shadow-[inset_0_1px_0_var(--hi-top),var(--sh-lg)]">
          {children}
        </div>
        <p className="mt-5 text-center text-[12px] text-[var(--tx-faint)]">
          Questions?{' '}
          <a className="text-[var(--accent-ink)] hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </div>
    </div>
  );
}
